import * as decoding from "lib0/decoding";
import * as encoding from "lib0/encoding";
import { useEffect, useRef, useState } from "react";
import * as syncProtocol from "y-protocols/sync";
import * as Y from "yjs";
import { useToken } from "../../../server/auth/use-token";
import { useFlag } from "../../../server/optimizely";
import { serviceURL } from "../../../utils/service-url";
import { isSeeded } from "../components/manager-v2/reducer/segment-doc";

import type { UUID } from "../../../utils/uuid";

// Close codes sent by segment-engine.
export const TOKEN_EXPIRED = 4001;
export const FORBIDDEN = 4003;

// The engine answers "ping" with "pong" without waking, so this keeps idle sockets open for free.
export const PING_INTERVAL = 30_000;

// After the connection drops for any other reason. Edits made meanwhile stay in the document and
// are exchanged when the sync restarts.
export const RECONNECT_DELAYS = [1000, 2000, 5000, 10_000];

// y-protocols message type, and y-partyserver's prefix for text messages that aren't Yjs.
const MESSAGE_SYNC = 0;
const CUSTOM = "__YPS:";

// Marks updates that came from the engine, so they aren't sent back to it.
const REMOTE = Symbol("segment-engine");

export const segmentEngineSocketURL = (segmentId: UUID): URL => {
  const url = new URL(segmentId, serviceURL("segment-engine"));
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url;
};

function syncMessage(write: (encoder: encoding.Encoder) => void): Uint8Array {
  const encoder = encoding.createEncoder();
  encoding.writeVarUint(encoder, MESSAGE_SYNC);
  write(encoder);
  return encoding.toUint8Array(encoder);
}

/**
 * Keeps `doc` in sync with the segment-engine Durable Object until the returned function is
 * called: authenticates with the token, reconnects when the token expires or the connection
 * drops, and seeds the engine's document if it turns out to be empty.
 */
function connectToEngine({
  segmentId,
  doc,
  getToken,
  getSeed,
  onSynced,
}: {
  segmentId: UUID;
  doc: Y.Doc;
  getToken: () => Promise<string>;
  getSeed: () => unknown;
  /** Called with true once the document has everything the engine has, false when that stops. */
  onSynced: (synced: boolean) => void;
}): () => void {
  let closed = false;
  let synced = false;
  let attempts = 0;
  let socket: WebSocket | undefined;
  let ping: ReturnType<typeof setInterval> | undefined;
  let retry: ReturnType<typeof setTimeout> | undefined;

  const send = (message: Uint8Array | string) => {
    if (socket?.readyState === WebSocket.OPEN) socket.send(message);
  };

  const sendLocalUpdate = (update: Uint8Array, origin: unknown) => {
    if (origin !== REMOTE) send(syncMessage((e) => syncProtocol.writeUpdate(e, update)));
  };
  doc.on("update", sendLocalUpdate);

  const receive = (data: ArrayBuffer) => {
    const decoder = decoding.createDecoder(new Uint8Array(data));
    if (decoding.readVarUint(decoder) !== MESSAGE_SYNC) return;

    const encoder = encoding.createEncoder();
    encoding.writeVarUint(encoder, MESSAGE_SYNC);
    const type = syncProtocol.readSyncMessage(decoder, encoder, doc, REMOTE);
    if (encoding.length(encoder) > 1) send(encoding.toUint8Array(encoder));

    // Step 2 answers our step 1: we now have everything the engine has.
    if (type === syncProtocol.messageYjsSyncStep2 && !synced) {
      synced = true;
      const seed = getSeed();
      if (!isSeeded(doc) && seed) send(`${CUSTOM}seed:${JSON.stringify(seed)}`);
      onSynced(true);
    }
  };

  const reconnect = (code: number) => {
    if (code === FORBIDDEN) {
      console.error("segment-engine: forbidden");
    } else if (code === TOKEN_EXPIRED) {
      void connect();
    } else {
      const delay = RECONNECT_DELAYS[Math.min(attempts, RECONNECT_DELAYS.length - 1)];
      attempts += 1;
      retry = setTimeout(() => void connect(), delay);
    }
  };

  const connect = async () => {
    const token = await getToken();
    if (closed) return;

    // Browsers can't set Authorization on a WebSocket, so the token goes as a subprotocol.
    const current = new WebSocket(segmentEngineSocketURL(segmentId), ["bearer", token]);
    // Yjs messages are binary, and Blob (the default) can only be read asynchronously.
    current.binaryType = "arraybuffer";
    socket = current;

    current.addEventListener("open", () => {
      attempts = 0;
      send(syncMessage((e) => syncProtocol.writeSyncStep1(e, doc)));
      ping = setInterval(() => send("ping"), PING_INTERVAL);
    });
    current.addEventListener("message", (event: MessageEvent<ArrayBuffer | string>) => {
      if (typeof event.data !== "string") receive(event.data);
      else if (event.data === `${CUSTOM}seed-rejected`) {
        console.error("segment-engine: seed rejected");
      }
    });
    current.addEventListener("error", (event) =>
      console.error("segment-engine socket error", event),
    );
    current.addEventListener("close", (event) => {
      clearInterval(ping);
      if (closed) return;
      synced = false;
      onSynced(false);
      reconnect(event.code);
    });
  };

  void connect();

  return () => {
    closed = true;
    clearInterval(ping);
    clearTimeout(retry);
    doc.off("update", sendLocalUpdate);
    socket?.close();
  };
}

export interface SegmentEngineDoc {
  /** The shared document; null when not connecting (flag off, no segment). */
  doc: Y.Doc | null;
  /** Synced with the engine and holding a segment, so it can be shown and edited. */
  ready: boolean;
  /** Connecting, or reconnecting, to the engine, so not ready yet. */
  connecting: boolean;
  /** Changes on every update, for memoising what is read from the document. */
  version: number;
}

/**
 * Opens a WebSocket to the segment-engine Durable Object for this segment and keeps a Yjs
 * document in sync with it. If the engine's document is empty, it is seeded with `seed`, what the
 * form holds for the segment.
 */
export const useSegmentEngineSocket = (
  segmentId: UUID | null,
  seed: unknown = null,
): SegmentEngineDoc => {
  const [enabled] = useFlag("cha_4571_segment_engine_websocket");
  const getToken = useToken();
  const [state, setState] = useState<Omit<SegmentEngineDoc, "connecting">>({
    doc: null,
    ready: false,
    version: 0,
  });

  // Read when the engine turns out to be empty, so a new seed doesn't reconnect.
  const seedRef = useRef(seed);
  useEffect(() => {
    seedRef.current = seed;
  }, [seed]);

  useEffect(() => {
    if (!enabled || !segmentId) return undefined;

    const doc = new Y.Doc();
    let synced = false;
    const publish = () =>
      setState((current) => ({
        doc,
        ready: synced && isSeeded(doc),
        version: current.version + 1,
      }));
    doc.on("update", publish);

    const disconnect = connectToEngine({
      segmentId,
      doc,
      getToken,
      getSeed: () => seedRef.current,
      onSynced: (value) => {
        synced = value;
        publish();
      },
    });

    return () => {
      disconnect();
      doc.destroy();
      setState((current) => ({ doc: null, ready: false, version: current.version + 1 }));
    };
  }, [enabled, segmentId, getToken]);

  return { ...state, connecting: Boolean(enabled && segmentId) && !state.ready };
};
